// DesktopEnvironment.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebaseConfig";
import BootScreen from "../components/layout/BootScreen";
import WindowFrame from "../components/ui/WindowFrame/WindowFrame";
import GrantApplicationForm from "../components/grant/GrantApplicationForm";
import LPApplication from "./standalone/LPApplication";
import LimitedPartnerPortal from "../components/lp/LimitedPartnerPortal";
import BrowserWindow from "../components/layout/BrowserWindow";
import BuddyMessenger from "../components/community/BuddyMessenger";
import FounderMap from "../components/community/FounderMap";
import MobileLanding from "../components/layout/MobileLanding";
import DesktopTaskbar from "../components/ui/Taskbar/DesktopTaskbar";
import OSIcon32 from "../components/icons/OSIcon32";
import { START_APPS, SOCIAL_LINKS, DESKTOP_LAUNCH_KEY } from "../data/desktopPrograms";
import "../styles/App.css";

const MESSENGER_LAST_SEEN_KEY = "gnf-buddy-messenger-last-seen";

// Metadata for every window the desktop can open — drives the taskbar
// buttons and the Start menu. Order here is Start-menu order.
const WINDOW_META = {
  website: { label: "Neighborhood Navigator", icon: "navigator" },
  submit: { label: "Submit pitch", icon: "pitch" },
  lpApplication: { label: "LP application", icon: "note" },
  lpPortal: { label: "LP portal", icon: "portal" },
  buddyMessenger: { label: "Buddy Messenger", icon: "messenger" },
  founderMap: { label: "Awardee map", icon: "map" },
};

// Desktop icon column, top to bottom.
const DESKTOP_ICONS = [
  { id: "myChapter", label: "My Chapter", icon: "computer" },
  { id: "website", label: "Neighborhood Navigator", icon: "navigator" },
  { id: "submit", label: "Submit Pitch", icon: "pitch" },
  { id: "founderMap", label: "Awardee Map", icon: "map" },
  { id: "buddyMessenger", label: "Buddy Messenger", icon: "messenger" },
  { id: "lpPortal", label: "LP Portal", icon: "portal" },
];

const SocialGlyph = ({ path }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d={path} />
  </svg>
);

const START_LINKS = SOCIAL_LINKS.map((link) => ({
  label: link.label,
  href: link.href,
  icon: <SocialGlyph path={link.path} />,
}));

export default function DesktopEnvironment() {
  // Skip the boot screen if the user has already booted this session
  // (e.g. they're navigating back from /pitch or /terms).
  const hasBooted = (() => {
    try {
      return sessionStorage.getItem("gnf-has-booted") === "1";
    } catch {
      return false;
    }
  })();

  // One-shot launch handoff from standalone pages (the portal's Start menu
  // and Close button write it before navigating here): an app id to open on
  // arrival, or "none" for an empty desktop. Read into state once; the key
  // is cleared in a mount effect so a manual refresh gets the default desktop.
  const [launchRequest] = useState(() => {
    try {
      return sessionStorage.getItem(DESKTOP_LAUNCH_KEY);
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.removeItem(DESKTOP_LAUNCH_KEY);
    } catch {}
  }, []);
  const startEmpty = launchRequest === "none";

  // State management
  const [bootSequence, setBootSequence] = useState(hasBooted ? "complete" : "booting");
  const [openApp, setOpenApp] = useState(startEmpty ? null : "website");
  const [currentTime, setCurrentTime] = useState({ time: "", date: "", dateCompact: "" });
  const [desktopReady, setDesktopReady] = useState(hasBooted);
  const [showMessenger, setShowMessenger] = useState(false);
  const [showFounderMap, setShowFounderMap] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openApps, setOpenApps] = useState(startEmpty ? [] : ['website']); // Track all open apps
  const [lpApplicationChapter, setLpApplicationChapter] = useState("");
  // Per-window minimised flags — the window hides but stays mounted; its
  // taskbar button stays and restores it.
  const [minimizedWindows, setMinimizedWindows] = useState({});
  const [selectedIcon, setSelectedIcon] = useState(null);
  // Imperative navigation into the Navigator window (desktop icons and the
  // tray donate shortcut land on a specific page). Nonce forces the effect
  // even when the same page is requested twice.
  const [browserPageRequest, setBrowserPageRequest] = useState(null);

  // Buddy Messenger unread tracking — persists per-device in localStorage so a
  // visitor coming back in a new tab still sees what's new since they last looked.
  const [recentMessages, setRecentMessages] = useState([]);
  const [lastSeenMessageAt, setLastSeenMessageAt] = useState(() => {
    try {
      const stored = localStorage.getItem(MESSENGER_LAST_SEEN_KEY);
      if (stored) return Number(stored);
    } catch {}
    // First visit: anchor "seen" to now so old messages don't all show as unread.
    const now = Date.now();
    try {
      localStorage.setItem(MESSENGER_LAST_SEEN_KEY, String(now));
    } catch {}
    return now;
  });

  // Improved z-index management - Set website to have a higher initial z-index
  // Use a ref for the counter so rapid successive opens can't read a stale value.
  const zCounterRef = useRef(1100);
  const [windowZIndexes, setWindowZIndexes] = useState({
    website: 1100, // Higher initial z-index for the browser window
    lpPortal: 1000,
    submit: 1000,
    lpApplication: 1000,
    buddyMessenger: 1000,
    founderMap: 1000,
    musicPlayer: 1000
  });

  // Cascade tracking — each open window gets a slot so they fan out
  // diagonally instead of perfectly overlapping. Slots are released on close
  // and the counter resets when the desktop has no open windows.
  const [cascadeSlots, setCascadeSlots] = useState(startEmpty ? {} : { website: 0 });
  const nextCascadeStepRef = useRef(startEmpty ? 0 : 1);
  const CASCADE_STEP_PX = 30;
  const CASCADE_WRAP = 6; // wrap so offsets stay on-screen after many opens

  // Improved bringToFront function
  const bringWindowToFront = (windowId) => {
    zCounterRef.current += 10;
    const newZIndex = zCounterRef.current;
    setWindowZIndexes(prev => ({
      ...prev,
      [windowId]: newZIndex
    }));
  };

  const assignCascadeSlot = (windowId) => {
    setCascadeSlots(prev => {
      if (prev[windowId] !== undefined) return prev; // keep existing slot
      const slot = nextCascadeStepRef.current % CASCADE_WRAP;
      nextCascadeStepRef.current += 1;
      return { ...prev, [windowId]: slot };
    });
  };

  const releaseCascadeSlot = (windowId) => {
    setCascadeSlots(prev => {
      if (prev[windowId] === undefined) return prev;
      const next = { ...prev };
      delete next[windowId];
      if (Object.keys(next).length === 0) {
        nextCascadeStepRef.current = 0;
      }
      return next;
    });
  };

  const cascadeOffsetFor = (windowId) =>
    (cascadeSlots[windowId] ?? 0) * CASCADE_STEP_PX;

  const minimizeWindow = (windowId) => {
    setMinimizedWindows(prev => ({ ...prev, [windowId]: true }));
  };

  const restoreWindow = (windowId) => {
    setMinimizedWindows(prev => ({ ...prev, [windowId]: false }));
    bringWindowToFront(windowId);
  };

  const clearMinimized = (windowId) => {
    setMinimizedWindows(prev => {
      if (!(windowId in prev)) return prev;
      const next = { ...prev };
      delete next[windowId];
      return next;
    });
  };

  // Check for mobile devices
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // iPads (and other tablets) get the full desktop experience — only phones
      // and genuinely small touch viewports route to MobileLanding.
      const isPhoneUA = /iphone|ipod|android.*mobile/i.test(userAgent);
      if (isPhoneUA || (window.innerWidth <= 768 && isTouchDevice)) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Clock functionality
  const updateClock = () => {
    const now = new Date();
    const timeStr = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const dateStr = now.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    setCurrentTime({
      time: timeStr,
      // hardcoded year — part of the Y2K-era aesthetic; the taskbar picks
      // the full or compact form by viewport width
      date: `${dateStr} 2003`,
      dateCompact: `${now.getMonth() + 1}/${now.getDate()}/03`,
    });
  };

  useEffect(() => {
    if (desktopReady) {
      updateClock();
      const interval = setInterval(updateClock, 1000 * 60);
      return () => clearInterval(interval);
    }
  }, [desktopReady]);

  // NEW EFFECT: Bring website window to front when desktop is ready
  useEffect(() => {
    if (desktopReady) {
      // Small delay to ensure all windows are rendered
      setTimeout(() => {
        bringWindowToFront("website");
      }, 100);
    }
  }, [desktopReady]); // Only run when desktop becomes ready

  // Subscribe to recent buddy messages so the tray badge updates live.
  useEffect(() => {
    if (!desktopReady) return;
    const q = query(
      collection(db, "guestMessages"),
      orderBy("timestamp", "desc"),
      limit(50)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRecentMessages(
          snap.docs.map((d) => {
            const data = d.data();
            const ts = data.timestamp?.toMillis?.() ?? 0;
            return { username: data.username, ts };
          })
        );
      },
      (err) => console.error("guestMessages snapshot failed:", err)
    );
    return unsub;
  }, [desktopReady]);

  // Count messages newer than lastSeen, excluding ones the visitor sent.
  const unreadMessengerCount = useMemo(() => {
    if (showMessenger) return 0;
    let myUsername = "";
    try {
      myUsername = sessionStorage.getItem("username") || "";
    } catch {}
    return recentMessages.filter(
      (m) => m.ts > lastSeenMessageAt && m.username !== myUsername
    ).length;
  }, [recentMessages, lastSeenMessageAt, showMessenger]);

  // When the messenger is open, treat anything we can see as seen — covers
  // both "user opens the window" and "new message arrives while it's open".
  useEffect(() => {
    if (!showMessenger || recentMessages.length === 0) return;
    const newest = recentMessages.reduce((max, m) => (m.ts > max ? m.ts : max), 0);
    if (newest > lastSeenMessageAt) {
      setLastSeenMessageAt(newest);
      try {
        localStorage.setItem(MESSENGER_LAST_SEEN_KEY, String(newest));
      } catch {}
    }
  }, [showMessenger, recentMessages, lastSeenMessageAt]);

  // Boot sequence handling
  const handleBootProgress = (isPreloading) => {
    if (isPreloading) {
      setBootSequence("preloaded");
    } else {
      setBootSequence("complete");
      setTimeout(() => {
        setDesktopReady(true);
        try {
          sessionStorage.setItem("gnf-has-booted", "1");
        } catch {}
      }, 300);
    }
  };

  // App opening with sound effects
  const handleAppOpen = (appName) => {
    const clickSound = document.getElementById("click-sound");
    if (clickSound) {
      clickSound.volume = 0.25;
      clickSound.currentTime = 0;
      clickSound.play().catch(() => {
        // Silent catch for browsers that block autoplay
      });
    }
    setOpenApp(appName);
    assignCascadeSlot(appName);
    clearMinimized(appName);
    if (!openApps.includes(appName)) {
      setOpenApps([...openApps, appName]);
    }
    // Defer bringToFront so it runs after any click-bubble bringToFront calls
    // from the triggering window (e.g. clicking a CTA inside the Navigator
    // bubbles up to WindowFrame.onClick, which would otherwise re-front the
    // Navigator and leave the new window behind it).
    queueMicrotask(() => bringWindowToFront(appName));
  };

  const openMessenger = () => {
    const buddySound = document.getElementById("buddyin-sound");
    if (buddySound) {
      buddySound.volume = 0.25;
      buddySound.currentTime = 0;
      buddySound.play().catch(() => {});
    }
    setShowMessenger(true);
    assignCascadeSlot('buddyMessenger');
    clearMinimized('buddyMessenger');
    bringWindowToFront('buddyMessenger');
  };

  const openFounderMap = () => {
    setShowFounderMap(true);
    assignCascadeSlot('founderMap');
    clearMinimized('founderMap');
    bringWindowToFront('founderMap');
  };

  const navigateNavigator = (page) => {
    setBrowserPageRequest({ page, nonce: Date.now() });
    handleAppOpen('website');
  };

  // Launching programs — desktop icons and the Start menu share this.
  const launchApp = (appId) => {
    switch (appId) {
      case 'myChapter':
        navigateNavigator('chapters');
        break;
      case 'website':
        handleAppOpen('website');
        break;
      case 'submit':
        handleAppOpen('submit');
        break;
      case 'buddyMessenger':
        openMessenger();
        break;
      case 'founderMap':
        openFounderMap();
        break;
      case 'lpPortal':
        // Always navigate to the standalone portal page (handles its own auth)
        window.location.href = '/portal';
        break;
      default:
        break;
    }
  };

  // Honor an app-launch handoff once the desktop is up. "website" and "none"
  // are already handled by the initial state above; everything else goes
  // through the same launcher the Start menu uses.
  const launchHandledRef = useRef(false);
  useEffect(() => {
    if (!desktopReady || launchHandledRef.current) return;
    launchHandledRef.current = true;
    if (launchRequest && launchRequest !== "none" && launchRequest !== "website") {
      launchApp(launchRequest);
    }
  }, [desktopReady]);

  // The taskbar is a view of the window state: one button per open window,
  // in open order.
  const taskbarWindows = [
    ...openApps.filter((id) => WINDOW_META[id]).map((id) => ({
      id,
      ...WINDOW_META[id],
      minimized: !!minimizedWindows[id],
    })),
    ...(showMessenger
      ? [{ id: 'buddyMessenger', ...WINDOW_META.buddyMessenger, minimized: !!minimizedWindows.buddyMessenger }]
      : []),
    ...(showFounderMap
      ? [{ id: 'founderMap', ...WINDOW_META.founderMap, minimized: !!minimizedWindows.founderMap }]
      : []),
  ];

  // Focused window: highest z-index among visible windows.
  const activeWindowId = taskbarWindows
    .filter((w) => !w.minimized)
    .reduce(
      (top, w) =>
        top === null || (windowZIndexes[w.id] ?? 0) > (windowZIndexes[top] ?? 0)
          ? w.id
          : top,
      null
    );

  const handleTaskbarWindowClick = (windowId) => {
    if (minimizedWindows[windowId]) {
      restoreWindow(windowId);
    } else if (windowId === activeWindowId) {
      minimizeWindow(windowId);
    } else {
      bringWindowToFront(windowId);
    }
  };

  const handleDesktopIconKeyDown = (e, appId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelectedIcon(appId);
      launchApp(appId);
    }
  };

  // On mobile, don't skip the boot sequence
  if (isMobile) {
    return <MobileLanding initialBootDone={false} />;
  }

  // Boot screen only for desktop
  if (bootSequence !== "complete" || !desktopReady) {
    return <BootScreen onFinish={handleBootProgress} />;
  }

  // Main desktop UI
  return (
    <div
      className="desktop"
      style={{ backgroundImage: 'url("/assets/wallpaper-clouds.webp")' }}
      onClick={() => setSelectedIcon(null)}
    >
      {/* Sound effects */}
      <audio id="click-sound" src="/sounds/click-on.mp3" preload="auto" />
      <audio id="buddyin-sound" src="/sounds/buddyIn.mp3" preload="auto" />

      {/* Desktop icon column */}
      <div className="win95-desktop-icons">
        {DESKTOP_ICONS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`win95-desktop-icon ${selectedIcon === item.id ? "is-selected" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIcon(item.id);
            }}
            onDoubleClick={() => launchApp(item.id)}
            onKeyDown={(e) => handleDesktopIconKeyDown(e, item.id)}
            aria-label={`Open ${item.label}`}
          >
            <span className="win95-desktop-icon__art">
              <OSIcon32 kind={item.icon} size={32} />
            </span>
            <span className="win95-desktop-icon__label">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Application Windows */}
      {openApps.includes("submit") && (
        <GrantApplicationForm
          onClose={() => {
            setOpenApps(prev => prev.filter(app => app !== 'submit'));
            if (openApp === 'submit') setOpenApp(null);
            releaseCascadeSlot('submit');
            clearMinimized('submit');
          }}
          zIndex={windowZIndexes.submit}
          windowId="submit"
          bringToFront={bringWindowToFront}
          cascadeOffset={cascadeOffsetFor('submit')}
          minimized={!!minimizedWindows.submit}
          onMinimize={() => minimizeWindow('submit')}
        />
      )}

      {openApps.includes("website") && (
        <WindowFrame
          title="Neighborhood Navigator — Good Neighbor Fund"
          icon="navigator"
          onClose={() => {
            setOpenApps(prev => prev.filter(app => app !== 'website'));
            if (openApp === 'website') setOpenApp(null);
            releaseCascadeSlot('website');
            clearMinimized('website');
          }}
          width={1200}
          height={840}
          center
          noPadding
          windowId="website"
          zIndex={windowZIndexes.website}
          bringToFront={bringWindowToFront}
          cascadeOffset={cascadeOffsetFor('website')}
          minimized={!!minimizedWindows.website}
          onMinimize={() => minimizeWindow('website')}
        >
          <BrowserWindow
            onClose={() => {
              setOpenApps(prev => prev.filter(app => app !== 'website'));
              if (openApp === 'website') setOpenApp(null);
              releaseCascadeSlot('website');
              clearMinimized('website');
            }}
            onPitchClick={() => handleAppOpen("submit")}
            onLpApplicationClick={(chapter) => {
              setLpApplicationChapter(chapter || "");
              handleAppOpen("lpApplication");
            }}
            pageRequest={browserPageRequest}
          />
        </WindowFrame>
      )}

      {openApps.includes("lpApplication") && (
        <WindowFrame
          title="Limited Partner application"
          icon="note"
          titlebarVariant="grape"
          onClose={() => {
            setOpenApps(prev => prev.filter(app => app !== 'lpApplication'));
            if (openApp === 'lpApplication') setOpenApp(null);
            releaseCascadeSlot('lpApplication');
            clearMinimized('lpApplication');
          }}
          width={720}
          height={Math.min(900, window.innerHeight - 80)}
          center
          noPadding
          windowId="lpApplication"
          zIndex={windowZIndexes.lpApplication}
          bringToFront={bringWindowToFront}
          cascadeOffset={cascadeOffsetFor('lpApplication')}
          minimized={!!minimizedWindows.lpApplication}
          onMinimize={() => minimizeWindow('lpApplication')}
        >
          <LPApplication
            onClose={() => {
              setOpenApps(prev => prev.filter(app => app !== 'lpApplication'));
              if (openApp === 'lpApplication') setOpenApp(null);
              releaseCascadeSlot('lpApplication');
              clearMinimized('lpApplication');
            }}
            initialChapter={lpApplicationChapter}
            hideFrame
          />
        </WindowFrame>
      )}

      {openApps.includes("lpPortal") && (
        <WindowFrame
          title="LP Portal"
          icon="portal"
          titlebarVariant="grape"
          onClose={() => {
            setOpenApps(prev => prev.filter(app => app !== 'lpPortal'));
            if (openApp === 'lpPortal') setOpenApp(null);
            releaseCascadeSlot('lpPortal');
            clearMinimized('lpPortal');
          }}
          width={1080}
          height={760}
          center
          isLPPortal
          windowId="lpPortal"
          zIndex={windowZIndexes.lpPortal}
          bringToFront={bringWindowToFront}
          cascadeOffset={cascadeOffsetFor('lpPortal')}
          minimized={!!minimizedWindows.lpPortal}
          onMinimize={() => minimizeWindow('lpPortal')}
        >
          <LimitedPartnerPortal onOpenGNFWebsite={() => handleAppOpen("website")} />
        </WindowFrame>
      )}

      {showMessenger && (
        <WindowFrame
          title="Buddy Messenger"
          icon="messenger"
          titlebarVariant="grape"
          onClose={() => {
            setShowMessenger(false);
            releaseCascadeSlot('buddyMessenger');
            clearMinimized('buddyMessenger');
          }}
          width={490}
          height={400}
          initialPosition={{ x: 40, y: window.innerHeight - 500 }}
          windowId="buddyMessenger"
          zIndex={windowZIndexes.buddyMessenger}
          bringToFront={bringWindowToFront}
          cascadeOffset={cascadeOffsetFor('buddyMessenger')}
          minimized={!!minimizedWindows.buddyMessenger}
          onMinimize={() => minimizeWindow('buddyMessenger')}
        >
          <BuddyMessenger onClose={() => {
            setShowMessenger(false);
            releaseCascadeSlot('buddyMessenger');
            clearMinimized('buddyMessenger');
          }} />
        </WindowFrame>
      )}

      {showFounderMap && (
        <WindowFrame
          title="Awardee Map"
          icon="map"
          titlebarVariant="tangerine"
          onClose={() => {
            setShowFounderMap(false);
            releaseCascadeSlot('founderMap');
            clearMinimized('founderMap');
          }}
          width={1200}
          height={840}
          center
          windowId="founderMap"
          zIndex={windowZIndexes.founderMap}
          bringToFront={bringWindowToFront}
          cascadeOffset={cascadeOffsetFor('founderMap')}
          minimized={!!minimizedWindows.founderMap}
          onMinimize={() => minimizeWindow('founderMap')}
        >
          <FounderMap
            onClose={() => {
              setShowFounderMap(false);
              releaseCascadeSlot('founderMap');
              clearMinimized('founderMap');
            }}
            windowId="founderMap"
            bringToFront={bringWindowToFront}
            isEmbedded={true}
          />
        </WindowFrame>
      )}

      {/* Taskbar */}
      <DesktopTaskbar
        windows={taskbarWindows}
        activeId={activeWindowId}
        onWindowButtonClick={handleTaskbarWindowClick}
        startApps={START_APPS}
        onStartApp={launchApp}
        links={START_LINKS}
        onTrayMessenger={() => {
          if (showMessenger && !minimizedWindows.buddyMessenger) {
            bringWindowToFront('buddyMessenger');
          } else {
            openMessenger();
          }
        }}
        onTrayDonate={() => navigateNavigator('donate')}
        unreadMessengerCount={unreadMessengerCount}
        currentTime={currentTime}
      />
    </div>
  );
}
